import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { fieldLabel, inputBase } from '@/lib/form-styles'
import { createOffer, type CalcOfferDraft } from '../api/calculation-offer.api'
import { fetchRaster } from '../api/calculation-positions.api'
import type { CalcRaster, Calculation } from '../types/calculation.types'
import { PartnerAuswahl } from './PartnerAuswahl'

/**
 * AP-37 4.14 Weg 2 — **Angebot erstellen**: Umfang, Kunde (ein Interessent
 * wird hier Kunde — Pflicht), Gruppen als Titel, Langtexte, Eventual- und
 * Wahlpositionen, „als abgegeben kennzeichnen". Nachlass und Bindefrist kommen
 * aus der Kalkulation. Ein Vorgang als Einheit mit dem Verb als Knopf (AP-36).
 */
export function AngebotErstellenDialog({ kalkulation: k, onClose, onDone }: { kalkulation: Calculation; onClose: () => void; onDone: (documentId: string) => void }) {
  const [raster, setRaster] = useState<CalcRaster | null>(null)
  const [nurAuswahl, setNurAuswahl] = useState(false)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [kunde, setKunde] = useState<{ id: string; name: string } | null>(k.client ? { id: k.client.id, name: k.client.name } : null)
  const [neuerKunde, setNeuerKunde] = useState(k.client ? '' : [k.prospectName, k.prospectCity].filter(Boolean).join(', '))
  const [titel, setTitel] = useState(true)
  const [langtexte, setLangtexte] = useState(true)
  const [eventual, setEventual] = useState(true)
  const inArbeit = k.status === 'IN_PROGRESS' && k.writable
  const [abgeben, setAbgeben] = useState(inArbeit)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    fetchRaster(k.id)
      .then(setRaster)
      .catch(() => setRaster(null))
  }, [k.id])

  const erstellen = async () => {
    setLaeuft(true)
    setFehler(null)
    const dto: CalcOfferDraft = {
      groupsAsTitles: titel,
      longTexts: langtexte,
      optionals: eventual,
      markSubmitted: inArbeit && abgeben,
      ...(nurAuswahl ? { positionIds: [...auswahl] } : {}),
      ...(kunde ? { clientId: kunde.id } : neuerKunde.trim() ? { newClientName: neuerKunde.trim() } : {}),
    }
    try {
      const r = await createOffer(k.id, dto)
      onDone(r.documentId)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Angebot ließ sich nicht erstellen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const haken = (wert: boolean, setze: (b: boolean) => void, text: string, hinweis?: string) => (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-0.5" checked={wert} onChange={(e) => setze(e.target.checked)} />
      <span>
        {text}
        {hinweis && <span className="block text-xs text-muted-foreground">{hinweis}</span>}
      </span>
    </label>
  )
  const positionen = (raster?.positions ?? []).filter((p) => p.kind !== 'TEXT')
  const kannErstellen = (kunde || neuerKunde.trim()) && (!nurAuswahl || auswahl.size > 0)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">Angebot erstellen</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
        <div className="space-y-4">
          <div>
            <span className={fieldLabel}>Kunde</span>
            <PartnerAuswahl wert={kunde} onWahl={(p) => setKunde(p ? { id: p.id, name: p.name } : null)} klasse={inputBase} />
            {!kunde && (
              <div className="mt-2">
                <label className="text-xs text-muted-foreground">
                  oder als neuen Kunden anlegen
                  <input className={`${inputBase} mt-1`} value={neuerKunde} onChange={(e) => setNeuerKunde(e.target.value)} placeholder="Name des Kunden" />
                </label>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <span className={fieldLabel}>Umfang</span>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="umfang" checked={!nurAuswahl} onChange={() => setNurAuswahl(false)} /> alle Positionen ({positionen.length})
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="umfang" checked={nurAuswahl} onChange={() => setNurAuswahl(true)} /> nur ausgewählte
            </label>
            {nurAuswahl && (
              <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border p-2">
                {positionen.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 py-0.5 text-sm">
                    <input
                      type="checkbox"
                      checked={auswahl.has(p.id)}
                      onChange={() => setAuswahl((a) => (a.has(p.id) ? new Set([...a].filter((x) => x !== p.id)) : new Set([...a, p.id])))}
                    />
                    <span className="w-12 tabular-nums text-muted-foreground">{p.displayNumber}</span>
                    <span className="flex-1 truncate">{p.shortText}</span>
                    <span className="tabular-nums">{geldText(p.totalPrice)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {haken(titel, setTitel, 'Gruppen als Titelzeilen')}
            {haken(langtexte, setLangtexte, 'Langtexte übernehmen')}
            {haken(eventual, setEventual, 'Eventual- und Wahlpositionen übernehmen', 'als optional bzw. alternativ gekennzeichnete Zeilen')}
            {inArbeit && haken(abgeben, setAbgeben, 'Kalkulation als abgegeben kennzeichnen', 'schreibt diese Fassung fest')}
          </div>

          <p className="text-xs text-muted-foreground">
            {k.discountPercent ? `Nachlass ${k.discountPercent.toLocaleString('de-DE')} % wird Belegrabatt. ` : ''}
            {k.validUntil ? `Gültig bis ${new Date(k.validUntil).toLocaleDateString('de-DE')} (Bindefrist). ` : ''}
            Danach ist das Angebot ein gewöhnlicher Beleg — die Kalkulation wird nicht nachgeführt.
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!kannErstellen || laeuft} onClick={() => void erstellen()}>
            Angebot erstellen
          </button>
        </div>
      </div>
    </div>
  )
}
